import { createAsyncThunk } from '@reduxjs/toolkit';

import { AdminApiPaths, APIDataProvider, TApiResponse } from '../../../api';
import {
  IPenaltyWaiver,
  TPenaltyWaiverAppealStatus,
  TPenaltyWaiverPaymentStatus,
  TPenaltyWaiverTutorAmountData,
  TPenaltyWaiverTypes,
} from '.';

export type TAdminCreatePenaltyWaiver = {
  sectionId: string;
  type: TPenaltyWaiverTypes;
  amount: number;
  currency: string;
  lessonHistoryId?: string;
  zoomRequestId?: string;
};

export type TAdminGetPenaltyWaivers = {
  limit: number;
  offset: number;
  sortKey?: string;
  sortValue?: number;
  lessonHistoryId?: string;
  zoomRequestId?: string;
  startDate?: string;
  endDate?: string;
  tutorId?: string;
  studentId?: string;
  courseId?: string;
  includeTutorAmountData?: boolean;
};

export type TAdminUpdatePenaltyWaiverTutorPaymentStatus = {
  penaltyWaiverIds: string[];
  paymentStatus: TPenaltyWaiverPaymentStatus;
};

export type TAdminGetPenaltyWaiverById = {
  id: string;
};

export type TAdminRespondToPenaltyAppeal = {
  penaltyWaiverId: string;
  adminNote: string;
  tutorNote: string;
  amount: number;
  response: Exclude<TPenaltyWaiverAppealStatus, 'PENDING'>;
};

const apiDataProvider = new APIDataProvider();

const adminGetPenaltyWaiversModule = async ({
  limit,
  offset,
  sortKey,
  sortValue,
  lessonHistoryId,
  zoomRequestId,
  startDate,
  endDate,
  tutorId,
  studentId,
  courseId,
  includeTutorAmountData,
}: TAdminGetPenaltyWaivers) => {
  let path = `${AdminApiPaths.penaltyWaivers}?limit=${limit}&offset=${offset}`;

  if (sortKey) {
    path += `&sortKey=${sortKey}`;
  }

  if (sortKey) {
    path += `&sortValue=${sortValue}`;
  }

  if (lessonHistoryId) {
    path += `&lessonHistoryId=${lessonHistoryId}`;
  }

  if (zoomRequestId) {
    path += `&zoomRequestId=${zoomRequestId}`;
  }

  if (startDate) {
    path += `&startDate=${startDate}`;
  }

  if (endDate) {
    path += `&endDate=${endDate}`;
  }

  if (tutorId) {
    path += `&tutorId=${tutorId}`;
  }

  if (studentId) {
    path += `&studentId=${studentId}`;
  }

  if (courseId) {
    path += `&courseId=${courseId}`;
  }

  if (includeTutorAmountData) {
    path += `&includeTutorAmountData=1`;
  }

  const response = await apiDataProvider.get<
    TApiResponse<{
      offset: number;
      limit: number;
      totalDocs: number;
      docs: Array<IPenaltyWaiver>;
      tutorAmountData?: TPenaltyWaiverTutorAmountData;
    }>
  >(path);

  return response.data;
};

export const adminCreatePenaltyWaiver = createAsyncThunk(
  '/admin/penalty-waiver/create',
  async (data: TAdminCreatePenaltyWaiver) => {
    const response = await apiDataProvider.post<TApiResponse<IPenaltyWaiver>>(
      `${AdminApiPaths.penaltyWaivers}/`,
      data
    );

    return response.data;
  }
);

export const adminGetPenaltyWaiverById = createAsyncThunk(
  '/admin/penalty-waiver/get-one',
  async ({ id }: TAdminGetPenaltyWaiverById) => {
    const response = await apiDataProvider.get<TApiResponse<IPenaltyWaiver>>(
      `${AdminApiPaths.penaltyWaivers}/${id}`
    );

    return response.data;
  }
);

export const adminUpdatePenaltyWaiverTutorPaymentStatus = createAsyncThunk(
  '/admin/penalty-waiver/update-tutor-payment-status',
  async (data: TAdminUpdatePenaltyWaiverTutorPaymentStatus) => {
    const response = await apiDataProvider.put<
      TApiResponse<TAdminUpdatePenaltyWaiverTutorPaymentStatus>
    >(`${AdminApiPaths.penaltyWaivers}/update-tutor-payment-status`, data);

    return response.data;
  }
);

export const adminRespondToPenaltyAppeal = createAsyncThunk(
  '/admin/penalty-waiver/respond',
  async (data: TAdminRespondToPenaltyAppeal) => {
    const response = await apiDataProvider.put<TApiResponse<IPenaltyWaiver>>(
      `${AdminApiPaths.penaltyWaivers}/${data.penaltyWaiverId}/appeal-respond`,
      data
    );

    return response.data;
  }
);

export const adminGetPenaltyWaivers = createAsyncThunk(
  '/admin/penalty-waiver/list',
  adminGetPenaltyWaiversModule
);
